/**
 * PaintedOverworldShell — controller scene for the painted-world overworld.
 *
 * SMA-42 Phase 2 deliverable. Replaces the tilemap OverworldScene as the
 * primary game world *when the painted-world feature flag is on*. This is a
 * non-rendering controller scene: the visible layer is whichever painted
 * scene is currently active (launched as a parallel Phaser scene). The
 * shell owns:
 *
 *   - The Colyseus connection to OverworldRoom (no new room is introduced
 *     per the SMA-42 constraint).
 *   - The `SceneRouter` that drives painted-scene navigation, configured
 *     with a `NetworkSceneTransitionRunner` so transitions only fire after
 *     the server has confirmed the zone change.
 *   - The `ZonePreloader`, wired to the server's connection graph (see
 *     `MSG_ZONE_GRAPH`).
 *   - The `RemotePlayerRenderer`, kept attached to whichever painted scene
 *     is active so other players show up at their `(zoneId, localX, localY)`.
 *   - A small per-frame throttled `MSG_LOCAL_POSITION_UPDATE` push so the
 *     server (and other clients) see the local walker move smoothly.
 *
 * The shell is registered last in `game.config.ts` so Phaser doesn't
 * auto-start it; TitleScene launches it on the painted-world path.
 */

import * as Phaser from 'phaser';
import { Client, Room } from 'colyseus.js';
import type { OverworldRoomState } from '@game/server/schemas';
import {
  MSG_ZONE_ENTER,
  MSG_ZONE_LEAVE,
  MSG_ZONE_TRANSITION_REQUEST,
  MSG_LOCAL_POSITION_UPDATE,
  MSG_ZONE_GRAPH,
  MSG_BUILD_STATE,
  MSG_BUILD_RESULT,
  MSG_BUILD_BLOCK_PLACED,
  MSG_BUILD_BLOCK_REMOVED,
  MSG_BUILD_CURRENCY_SYNC,
  COMBAT_PROTOCOL_VERSION,
  type ZoneEnterEvent,
  type ZoneLeaveEvent,
  type ZoneTransitionRequest,
  type LocalPositionUpdateEvent,
  type ZoneGraphPayload,
  type BuildStateEvent,
  type BuildResultEvent,
  type BuildBlockPlacedEvent,
  type BuildBlockRemovedEvent,
  type BuildCurrencySyncEvent,
} from '@game/shared';
import { COLYSEUS_URL } from '../../config/network';
import { getDemoUserJoinOptions } from '../../config/demoUsers';
import {
  PaintedScene,
  PAINTED_SCENE_EVENTS,
  PAINTED_SHELL_MANAGED_REGISTRY_KEY,
  type EdgeZoneEnteredEvent,
} from './PaintedScene';
import {
  loadJourneyManifest,
  SceneRouter,
  type SceneTransitionRunner,
  type SceneTransitionRequest,
} from './SceneRouter';
import { ZonePreloader, type ZoneTextureLoader } from './ZonePreloader';
import {
  PaintedSceneTransitionRunner,
} from './SceneTransition';
import { ZoneMessageBus, type ZoneMessageType } from './ZoneMessageBus';
import { RemotePlayerRenderer } from './RemotePlayerRenderer';
import { JourneyManifestSchema, type JourneyManifest } from './journeyManifest.schema';

export const PAINTED_OVERWORLD_SHELL_SCENE_KEY = 'PaintedOverworldShell' as const;

export interface PaintedOverworldShellInitData {
  /** Character name for Colyseus join. */
  characterName?: string;
  /** Character vocation (server uses for class assignment). */
  vocation?: string;
  /** Character realm of origin. */
  realm?: string;
  /** Save slot the player picked on Title (0-based). */
  slotIndex?: number;
}

/** Send the local player's position no more than this often. */
const POSITION_PUSH_INTERVAL_MS = 100;

/** Network transition runner — waits for server confirmation before swapping. */
class NetworkSceneTransitionRunner implements SceneTransitionRunner {
  private readonly visualRunner: PaintedSceneTransitionRunner;

  constructor(
    private readonly room: () => Room<OverworldRoomState> | null,
    visualRunner: PaintedSceneTransitionRunner,
  ) {
    this.visualRunner = visualRunner;
  }

  async run(req: SceneTransitionRequest): Promise<void> {
    const room = this.room();
    if (!room) {
      // Offline mode — fall back to the visual runner directly. Useful for
      // dev-tooling that boots the shell without a Colyseus connection.
      await this.visualRunner.run(req);
      return;
    }
    // The shell already issued the MSG_ZONE_TRANSITION_REQUEST and only
    // calls SceneRouter.goTo once MSG_ZONE_ENTER lands, so by the time we
    // reach `run()` the server is in agreement. Run the visual choreography.
    await this.visualRunner.run(req);
  }
}

export class PaintedOverworldShell extends Phaser.Scene {
  public static readonly KEY = PAINTED_OVERWORLD_SHELL_SCENE_KEY;

  private initData: PaintedOverworldShellInitData = {};
  private colyseusClient: Client | null = null;
  private room: Room<OverworldRoomState> | null = null;
  private localSessionId: string | null = null;

  private manifest: JourneyManifest | null = null;
  private router: SceneRouter | null = null;
  private preloader: ZonePreloader | null = null;
  private remoteRenderer: RemotePlayerRenderer | null = null;
  private messageBus = new ZoneMessageBus();
  private visualRunner: PaintedSceneTransitionRunner | null = null;

  /** Currently-active painted scene Phaser key. */
  private activePaintedKey: string | null = null;
  /** Lookup table built from the registered Phaser scene list. */
  private manifestToPhaser = new Map<string, string>();

  /** Throttle timer for outbound MSG_LOCAL_POSITION_UPDATE. */
  private lastPositionPushAt = 0;

  /** Outstanding zone transition request awaiting server confirm. */
  private pendingTransition: { destinationZoneId: string; edgeId: string } | null = null;

  constructor() {
    super({ key: PAINTED_OVERWORLD_SHELL_SCENE_KEY });
  }

  init(data?: PaintedOverworldShellInitData): void {
    this.initData = data ?? {};
    this.localSessionId = null;
    this.room = null;
    this.colyseusClient = null;
    this.manifest = null;
    this.router = null;
    this.preloader = null;
    this.remoteRenderer = null;
    this.messageBus.clear();
    this.visualRunner = null;
    this.activePaintedKey = null;
    this.lastPositionPushAt = 0;
    this.pendingTransition = null;
    this.manifestToPhaser = this.collectManifestToPhaserMap();
  }

  preload(): void {
    if (!this.cache.json.has('journey-manifest')) {
      this.load.json('journey-manifest', 'assets/scenes/journey-manifest.json');
    }
  }

  create(): void {
    // Mark that the shell is hosting painted scenes — this suppresses the
    // soft-fallback `requestTransition` swap inside PaintedScene so the
    // shell is the only thing that drives navigation.
    this.game.registry.set(PAINTED_SHELL_MANAGED_REGISTRY_KEY, true);

    this.bootRouter();
    this.bindGlobalEvents();
    this.connectToOverworld();
  }

  update(_time: number, delta: number): void {
    this.maybePushLocalPosition();
    if (this.remoteRenderer) {
      this.remoteRenderer.update(delta);
    }
  }

  // ── boot helpers ────────────────────────────────────────────────────────

  private bootRouter(): void {
    const raw = this.cache.json.get('journey-manifest');
    if (!raw) {
      console.error('[PaintedOverworldShell] journey-manifest not in cache');
      return;
    }
    let manifest: JourneyManifest;
    try {
      // Re-validate (defensive; the schema rejects malformed manifests).
      manifest = loadJourneyManifestFromValue(raw);
    } catch (err) {
      console.error('[PaintedOverworldShell] manifest invalid:', err);
      return;
    }
    this.manifest = manifest;

    const loader: ZoneTextureLoader = {
      isTextureLoaded: (sceneKey: string) => {
        const phaserKey = this.manifestToPhaser.get(sceneKey);
        if (!phaserKey) return false;
        // We treat "scene registered" as "loaded" for the LRU's purposes —
        // each PaintedScene preloads its own assets when launched. Fine
        // since the shell relies on Phaser's loader cache to dedupe the
        // actual texture I/O.
        return Boolean(this.scene.get(phaserKey));
      },
      loadZoneTexture: async (sceneKey: string) => {
        const phaserKey = this.manifestToPhaser.get(sceneKey);
        if (!phaserKey) return;
        // Preload the scene's spec JSON so the actual scene start is
        // instant. We don't launch the scene here — that would render it.
        const spec = manifest.scenes.find((s) => s.key === sceneKey);
        if (!spec) return;
        const cacheKey = `spec:${sceneKey}`;
        if (this.cache.json.has(cacheKey)) return;
        await new Promise<void>((resolve) => {
          this.load.json(cacheKey, spec.assetPath);
          this.load.once('complete', () => resolve());
          this.load.once('loaderror', () => resolve());
          this.load.start();
        });
      },
      unloadZoneTexture: (sceneKey: string) => {
        // Soft eviction — drop the spec JSON cache entry. Texture data is
        // managed by Phaser's cache and Sleep/Stop scene hooks.
        const cacheKey = `spec:${sceneKey}`;
        if (this.cache.json.has(cacheKey)) {
          this.cache.json.remove(cacheKey);
        }
      },
    };

    this.preloader = new ZonePreloader(manifest, loader);
    this.visualRunner = new PaintedSceneTransitionRunner(this.game, {
      manifestToPhaserKey: this.manifestToPhaser,
    });
    const networkRunner = new NetworkSceneTransitionRunner(
      () => this.room,
      this.visualRunner,
    );

    const slotIndex = this.initData.slotIndex ?? 0;
    this.router = new SceneRouter({
      manifest,
      transition: networkRunner,
      preloader: this.preloader,
      storageKey: `painted_scene_state_${slotIndex}`,
    });

    // Launch the initial painted scene as the visible layer.
    const initialKey = this.router.currentSceneKey();
    this.launchPaintedScene(initialKey);

    // Build the per-instance remote-player renderer.
    this.remoteRenderer = new RemotePlayerRenderer();
    this.attachRemoteRendererToActiveScene();
  }

  private collectManifestToPhaserMap(): Map<string, string> {
    const map = new Map<string, string>();
    if (!this.scene.manager) return map;
    for (const s of this.scene.manager.scenes) {
      if (s instanceof PaintedScene) {
        map.set(s.manifestKey, s.scene.key);
      }
    }
    return map;
  }

  private launchPaintedScene(manifestKey: string, spawn?: { x: number; y: number }): void {
    const phaserKey = this.manifestToPhaser.get(manifestKey);
    if (!phaserKey) {
      console.warn(`[PaintedOverworldShell] no Phaser scene registered for "${manifestKey}"`);
      return;
    }
    if (this.activePaintedKey === phaserKey) return;
    if (this.activePaintedKey) {
      // Old scene is torn down by the visual runner's transition; we only
      // call this directly on the very first launch.
      this.scene.stop(this.activePaintedKey);
    }
    this.scene.launch(phaserKey, spawn ? { spawn } : {});
    this.activePaintedKey = phaserKey;
    this.attachRemoteRendererToActiveScene();
    this.attachNetworkToActiveScene();
  }

  private attachRemoteRendererToActiveScene(): void {
    if (!this.remoteRenderer || !this.activePaintedKey) return;
    const scene = this.scene.get(this.activePaintedKey);
    if (scene instanceof PaintedScene) {
      this.remoteRenderer.attachTo(scene);
    }
  }

  private attachNetworkToActiveScene(): void {
    if (!this.activePaintedKey) return;
    const scene = this.scene.get(this.activePaintedKey);
    const connected = scene as unknown as {
      setMessageBus?: (bus: ZoneMessageBus) => void;
      setRoomSend?: (send: (type: string, data?: unknown) => void) => void;
    };
    connected.setMessageBus?.(this.messageBus);
    connected.setRoomSend?.((type, data) => this.room?.send(type, data));
  }

  private bindGlobalEvents(): void {
    this.game.events.on(
      PAINTED_SCENE_EVENTS.EDGE_ZONE_ENTERED,
      this.onEdgeZoneEntered,
      this,
    );
    this.game.events.on(
      PAINTED_SCENE_EVENTS.READY,
      this.onPaintedSceneReady,
      this,
    );
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.shutdown());
  }

  private onPaintedSceneReady = (payload: { sceneKey: string }): void => {
    if (!this.remoteRenderer || !this.activePaintedKey) return;
    const scene = this.scene.get(this.activePaintedKey);
    if (scene instanceof PaintedScene && scene.manifestKey === payload.sceneKey) {
      this.remoteRenderer.attachTo(scene);
    }
  };

  private onEdgeZoneEntered = (payload: EdgeZoneEnteredEvent): void => {
    if (!this.router) return;
    if (this.pendingTransition) return; // already awaiting a confirm
    if (!this.room) {
      // Offline / pre-connect — fall through to local-only navigation.
      void this.router.goToViaEdgeZone(payload.zone.id).catch(() => {
        // swallow — router rejects on missing edge zones, fine to ignore.
      });
      return;
    }
    this.pendingTransition = {
      destinationZoneId: payload.zone.targetScene,
      edgeId: payload.zone.id,
    };
    const req: ZoneTransitionRequest = {
      v: COMBAT_PROTOCOL_VERSION,
      edgeId: payload.zone.id,
      destinationZoneId: payload.zone.targetScene,
    };
    this.room.send(MSG_ZONE_TRANSITION_REQUEST, req);
  };

  // ── Colyseus ────────────────────────────────────────────────────────────

  private async connectToOverworld(): Promise<void> {
    try {
      this.colyseusClient = new Client(COLYSEUS_URL);
      const charName = this.initData.characterName
        ?? (this.registry.get('character:name') as string | undefined)
        ?? `Player-${Math.floor(Math.random() * 1000)}`;
      const charVocation = this.initData.vocation
        ?? (this.registry.get('character:vocation') as string | undefined);
      const charRealm = this.initData.realm
        ?? (this.registry.get('character:realm') as string | undefined);

      const joinOptions: Record<string, string> = { name: charName };
      if (charVocation) joinOptions.vocation = charVocation;
      if (charRealm) joinOptions.realm = charRealm;
      const demoUser = getDemoUserJoinOptions();
      if (demoUser) Object.assign(joinOptions, demoUser);

      this.room = await this.colyseusClient.joinOrCreate('overworld_room', joinOptions);
      this.localSessionId = this.room.sessionId;
      this.setupRoomListeners();
      // Signal anyone listening (e.g. TitleScene's seamless dissolve) that
      // the painted-world is ready for the cross-fade.
      this.registry.set('overworld:ready', true);
    } catch (err) {
      console.error('[PaintedOverworldShell] Failed to connect:', err);
      this.time.delayedCall(3000, () => {
        void this.connectToOverworld();
      });
    }
  }

  private setupRoomListeners(): void {
    if (!this.room) return;

    // Server-confirmed zone enters (both for self and remote players).
    this.room.onMessage(MSG_ZONE_ENTER, (msg: ZoneEnterEvent) => {
      if (msg.sessionId === this.localSessionId) {
        this.handleLocalZoneEnter(msg);
      } else {
        this.handleRemoteZoneEnter(msg);
      }
    });
    this.room.onMessage(MSG_ZONE_LEAVE, (msg: ZoneLeaveEvent) => {
      if (msg.sessionId === this.localSessionId) return; // local handled by ENTER reply
      this.handleRemoteZoneLeave(msg);
    });
    this.room.onMessage(MSG_ZONE_GRAPH, (msg: ZoneGraphPayload) => {
      if (this.preloader) {
        this.preloader.setConnectionGraph(msg.edges);
      }
    });
    this.room.onMessage(MSG_BUILD_STATE, (msg: BuildStateEvent) => {
      this.publishToActiveScene(MSG_BUILD_STATE, msg);
    });
    this.room.onMessage(MSG_BUILD_RESULT, (msg: BuildResultEvent) => {
      this.publishToActiveScene(MSG_BUILD_RESULT, msg);
    });
    this.room.onMessage(MSG_BUILD_BLOCK_PLACED, (msg: BuildBlockPlacedEvent) => {
      this.publishToActiveScene(MSG_BUILD_BLOCK_PLACED, msg);
    });
    this.room.onMessage(MSG_BUILD_BLOCK_REMOVED, (msg: BuildBlockRemovedEvent) => {
      this.publishToActiveScene(MSG_BUILD_BLOCK_REMOVED, msg);
    });
    this.room.onMessage(MSG_BUILD_CURRENCY_SYNC, (msg: BuildCurrencySyncEvent) => {
      this.publishToActiveScene(MSG_BUILD_CURRENCY_SYNC, msg);
    });

    // Remote player schema sync — track zone position changes.
    this.room.state.players.onAdd((player, sessionId) => {
      if (sessionId === this.localSessionId) return;
      this.publishRemoteFromSchema(sessionId, player);
      player.onChange(() => this.publishRemoteFromSchema(sessionId, player));
    });
    this.room.state.players.onRemove((_player, sessionId) => {
      if (this.remoteRenderer) {
        this.remoteRenderer.removeRemote(sessionId);
      }
    });
  }

  private publishRemoteFromSchema(
    sessionId: string,
    player: { name: string; sceneZoneId: string; localX: number; localY: number; attendanceState?: string },
  ): void {
    if (!this.remoteRenderer) return;
    this.remoteRenderer.upsertRemote({
      sessionId,
      name: player.name,
      zoneId: player.sceneZoneId,
      localX: player.localX,
      localY: player.localY,
      attendanceState: player.attendanceState,
    });
  }

  private handleLocalZoneEnter(msg: ZoneEnterEvent): void {
    if (!this.router) return;
    // Server has confirmed the zone change — drive SceneRouter.goTo so the
    // visual transition runner does the cross-fade. Use the pending edge
    // info so the transition kind/duration come from the manifest entry.
    const pending = this.pendingTransition;
    this.pendingTransition = null;

    void this.router
      .goTo(msg.zoneId, {
        ...(pending?.edgeId ? { viaEdgeZoneId: pending.edgeId } : {}),
        spawn: { x: msg.localX, y: msg.localY },
      })
      .then(() => {
        // After the visual swap, lookup the freshly-active painted scene
        // and re-attach the remote renderer.
        const phaserKey = this.manifestToPhaser.get(msg.zoneId);
        if (phaserKey) {
          this.activePaintedKey = phaserKey;
          this.attachRemoteRendererToActiveScene();
          this.attachNetworkToActiveScene();
        }
      })
      .catch((err) => {
        console.warn('[PaintedOverworldShell] router.goTo failed:', err);
      });
  }

  private handleRemoteZoneEnter(msg: ZoneEnterEvent): void {
    if (!this.remoteRenderer) return;
    // Look up the player's name and attendance state from the schema if available.
    const playerSchema = this.room?.state.players.get(msg.sessionId);
    const name = playerSchema?.name ?? msg.sessionId.slice(0, 6);
    const attendanceState = (playerSchema as { attendanceState?: string } | undefined)?.attendanceState;
    this.remoteRenderer.upsertRemote({
      sessionId: msg.sessionId,
      name,
      zoneId: msg.zoneId,
      localX: msg.localX,
      localY: msg.localY,
      attendanceState,
    });
  }

  private handleRemoteZoneLeave(msg: ZoneLeaveEvent): void {
    if (!this.remoteRenderer) return;
    // Player may still appear in the new zone after MSG_ZONE_ENTER lands;
    // we just drop the sprite from the previous zone. Idempotent.
    this.remoteRenderer.removeRemote(msg.sessionId);
  }

  private publishToActiveScene(type: string, payload: unknown): void {
    this.messageBus.publish(type as ZoneMessageType, payload as never);
  }

  // ── outbound throttle ───────────────────────────────────────────────────

  private maybePushLocalPosition(): void {
    if (!this.room || !this.activePaintedKey) return;
    const now = this.game.getTime();
    if (now - this.lastPositionPushAt < POSITION_PUSH_INTERVAL_MS) return;
    const scene = this.scene.get(this.activePaintedKey);
    if (!(scene instanceof PaintedScene) || !scene.ready) return;

    const localX = (scene as unknown as { walkerNX: number }).walkerNX ?? 0.5;
    const localY = (scene as unknown as { walkerNY: number }).walkerNY ?? 0.5;
    const payload: LocalPositionUpdateEvent = {
      v: COMBAT_PROTOCOL_VERSION,
      zoneId: scene.manifestKey,
      localX,
      localY,
    };
    this.room.send(MSG_LOCAL_POSITION_UPDATE, payload);
    this.lastPositionPushAt = now;
  }

  // ── teardown ────────────────────────────────────────────────────────────

  private shutdown(): void {
    this.game.events.off(
      PAINTED_SCENE_EVENTS.EDGE_ZONE_ENTERED,
      this.onEdgeZoneEntered,
      this,
    );
    this.game.events.off(
      PAINTED_SCENE_EVENTS.READY,
      this.onPaintedSceneReady,
      this,
    );
    if (this.remoteRenderer) {
      this.remoteRenderer.destroy();
      this.remoteRenderer = null;
    }
    if (this.room) {
      try { this.room.leave(); } catch { /* already left */ }
      this.room = null;
    }
    this.colyseusClient = null;
    this.game.registry.set(PAINTED_SHELL_MANAGED_REGISTRY_KEY, false);
  }
}

/**
 * Runtime manifest validation. PreloaderScene already preloaded the JSON
 * into the Phaser cache, so we re-parse it through the same Zod schema
 * `loadJourneyManifest` uses rather than fetching it again.
 */
function loadJourneyManifestFromValue(raw: unknown): JourneyManifest {
  return JourneyManifestSchema.parse(raw);
}

// Re-export for symmetry with existing scene barrel exports.
export { loadJourneyManifest };
